import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { database } from '../../firebase';
import { ref, get, set, update } from 'firebase/database';
import Swal from 'sweetalert2';
import {
  ShoppingBagIcon, ClockIcon, CheckCircleIcon, XCircleIcon,
  ArrowPathIcon, ChevronRightIcon, MagnifyingGlassIcon,
  TagIcon, CreditCardIcon, XMarkIcon, MapPinIcon,
  PhoneIcon, EnvelopeIcon, CalendarIcon, SparklesIcon,
  ExclamationTriangleIcon, FunnelIcon, Bars3Icon,
  ChevronLeftIcon, ChevronDownIcon, UserIcon,
  TruckIcon, CubeIcon, DocumentTextIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import {
  CheckCircleIcon as CheckCircleSolid,
  XCircleIcon as XCircleSolid,
} from '@heroicons/react/24/solid';

// ─── SWAL Z-INDEX ─────────────────────────────────────────────────────────────
if (typeof document !== 'undefined' && !document.getElementById('swal-op-z')) {
  const s = document.createElement('style');
  s.id = 'swal-op-z';
  s.textContent = `.swal2-container{z-index:999999!important}.swal2-on-top{z-index:999999!important}`;
  document.head.appendChild(s);
}
const toast = (icon, title, text = '') =>
  Swal.fire({ icon, title, text, timer: 2800, toast: true, position: 'top-end', showConfirmButton: false, customClass: { container: 'swal2-on-top' } });

// ─── WHATSAPP ICON ────────────────────────────────────────────────────────────
const WhatsAppIcon = ({ className = 'h-4 w-4' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// ─── CONFIG ───────────────────────────────────────────────────────────────────
const ORDER_STATUSES = ['pending','processed','ready','collected','cancelled'];

const ORDER_STATUS = {
  pending:   { label: 'Pending',    color: '#f59e0b', bg: '#fffbeb', border: '#fde68a' },
  processed: { label: 'Processing', color: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe' },
  ready:     { label: 'Ready',      color: '#8b5cf6', bg: '#f5f3ff', border: '#ddd6fe' },
  collected: { label: 'Completed',  color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  cancelled: { label: 'Cancelled',  color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
};

const PAYMENT_STATUS = {
  pending:  { label: 'Unpaid',   color: '#f59e0b', bg: '#fffbeb', border: '#fde68a' },
  paid:     { label: 'Paid',     color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
  failed:   { label: 'Failed',   color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
  refunded: { label: 'Refunded', color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb' },
};

const PAYMENT_METHODS = [
  { val: 'whatsapp_cod', label: 'WhatsApp / COD' },
  { val: 'mpesa',        label: 'M-Pesa'          },
  { val: 'cash',         label: 'Cash'            },
  { val: 'visa',         label: 'Visa/Mastercard' },
  { val: 'paystack',     label: 'Paystack'        },
  { val: 'wallet',       label: 'Wallet'          },
];

const ORDER_SOURCES = [
  { val: 'shop_card',           label: 'Shop Card / Modal' },
  { val: 'shop_cart_whatsapp',  label: 'Checkout WhatsApp' },
  { val: 'shop_cart_online',    label: 'Checkout Online'   },
];

const fmtDate = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' });
};
const fmt = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const fmtNum = (n) => (n ?? 0).toLocaleString();

// ─── STATUS BADGE ─────────────────────────────────────────────────────────────
const StatusBadge = ({ type, value, small = false }) => {
  const cfg = type === 'order' ? ORDER_STATUS[value] : PAYMENT_STATUS[value];
  if (!cfg) return <span className="text-xs text-gray-400">{value || '—'}</span>;
  return (
    <span className={`inline-flex items-center gap-1 font-semibold rounded-full border ${small ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2.5 py-1'}`}
      style={{ color: cfg.color, background: cfg.bg, borderColor: cfg.border }}>
      {cfg.label}
    </span>
  );
};

// ─── STOCK DECREMENT (on complete) ───────────────────────────────────────────
// Decrements stock for each item in the order, variant-aware.
const decrementStock = async (items) => {
  for (const item of items) {
    if (!item.productId) continue;
    try {
      const pRef  = ref(database, `products/${item.productId}`);
      const pSnap = await get(pRef);
      if (!pSnap.exists()) continue;

      const prod     = pSnap.val();
      const variants = JSON.parse(JSON.stringify(prod.variants || []));
      const qty      = item.quantity || 1;
      const color    = item.variant?.color || '';
      const size     = item.variant?.size  || '';

      if (prod.variantType === 'simple' && variants[0]) {
        variants[0].quantity = Math.max(0, (variants[0].quantity || 0) - qty);
      } else if (prod.variantType === 'size_only') {
        const vi = variants.findIndex(v => v.size === size);
        if (vi >= 0) variants[vi].quantity = Math.max(0, (variants[vi].quantity || 0) - qty);
      } else if (prod.variantType === 'color_size') {
        const vi = variants.findIndex(v => v.color === color);
        if (vi >= 0) {
          const si = (variants[vi].sizes || []).findIndex(s => s.size === size);
          if (si >= 0) variants[vi].sizes[si].quantity = Math.max(0, (variants[vi].sizes[si].quantity || 0) - qty);
        }
      }

      await update(pRef, { variants, updatedAt: Date.now() });
    } catch (e) { console.error(`Stock decrement error for ${item.productId}:`, e); }
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// ORDER DETAIL MODAL (full-screen slide-in)
// ═══════════════════════════════════════════════════════════════════════════════
const OrderDetailModal = ({ order: initialOrder, onClose, onUpdated }) => {
  const [order,       setOrder]      = useState(initialOrder);
  const [activeTab,   setActiveTab]  = useState('contact');
  const [saving,      setSaving]     = useState(false);
  const [completing,  setCompleting] = useState(false);
  const [cancelling,  setCancelling] = useState(false);

  const [contact, setContact] = useState({
    name:    order.userInfo?.name    || '',
    email:   order.userInfo?.email   || '',
    phone:   order.userInfo?.phone   || '',
    company: order.userInfo?.company || '',
  });
  const [delivery, setDelivery] = useState({
    address:     order.deliveryInfo?.address     || '',
    area:        order.deliveryInfo?.area        || '',
    city:        order.deliveryInfo?.city        || '',
    landmark:    order.deliveryInfo?.landmark    || '',
    notes:       order.deliveryInfo?.notes       || '',
    contactPref: order.deliveryInfo?.contactPref || 'phone',
  });
  const [payment, setPayment] = useState({
    paymentMethod:    order.paymentMethod    || 'whatsapp_cod',
    paymentStatus:    order.paymentStatus    || 'pending',
    paymentReference: order.paymentReference || '',
  });

  const isTerminal = order.orderStatus === 'collected' || order.orderStatus === 'cancelled';

  const tabs = [
    { key: 'contact',  label: 'Contact',  icon: <UserIcon       className="h-4 w-4" /> },
    { key: 'delivery', label: 'Delivery', icon: <TruckIcon      className="h-4 w-4" /> },
    { key: 'items',    label: 'Items',    icon: <CubeIcon       className="h-4 w-4" /> },
    { key: 'payment',  label: 'Payment',  icon: <CreditCardIcon className="h-4 w-4" /> },
    { key: 'timeline', label: 'Timeline', icon: <CalendarIcon   className="h-4 w-4" /> },
  ];

  const inputCls = 'w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm focus:outline-none transition-all';

  const saveChanges = async () => {
    setSaving(true);
    try {
      const updates = {
        userInfo:         { ...contact },
        deliveryInfo:     { ...delivery },
        paymentMethod:    payment.paymentMethod,
        paymentStatus:    payment.paymentStatus,
        paymentReference: payment.paymentReference,
      };
      await update(ref(database, `orders/${order.id}`), updates);
      const updated = { ...order, ...updates };
      setOrder(updated);
      onUpdated(updated);
      toast('success', 'Order updated');
    } catch (e) {
      console.error(e);
      toast('error', 'Could not save changes');
    } finally { setSaving(false); }
  };

  // ── COMPLETE ORDER ──────────────────────────────────────────────────────────
  const completeOrder = async () => {
    const result = await Swal.fire({
      title: 'Complete this order?',
      html: `
        <div style="text-align:center;padding:4px 0">
          <p style="color:#374151;font-size:14px;margin-bottom:8px">
            Completing <strong>${order.orderNumber}</strong> will:
          </p>
          <ul style="text-align:left;font-size:13px;color:#6b7280;list-style:disc;padding-left:20px;line-height:1.8">
            <li>Mark the order as <strong>Completed</strong></li>
            <li>Decrement stock for all ordered items</li>
            <li>This action cannot be undone</li>
          </ul>
        </div>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Complete Order',
      cancelButtonText: 'Not yet',
      confirmButtonColor: '#16a34a',
      cancelButtonColor: '#6b7280',
      customClass: { container: 'swal2-on-top', popup: 'rounded-2xl' },
    });

    if (!result.isConfirmed) return;
    setCompleting(true);

    try {
      const now = Date.now();
      const updates = {
        orderStatus:   'collected',
        paymentStatus: payment.paymentStatus === 'pending' ? 'paid' : payment.paymentStatus,
        collectedAt:   now,
        updatedAt:     now,
      };

      // 1. Update order status
      await update(ref(database, `orders/${order.id}`), updates);

      // 2. Decrement stock — variant-aware
      await decrementStock(order.items || []);

      // 3. Update recentOrders status for the user
      if (order.userId && order.userId !== 'guest') {
        await update(ref(database, `users/${order.userId}/recentOrders/${order.id}`), {
          status: 'collected',
        }).catch(() => {});
      }

      const updated = { ...order, ...updates };
      setOrder(updated);
      onUpdated(updated);
      toast('success', '✅ Order completed & stock decremented');
    } catch (e) {
      console.error(e);
      toast('error', 'Could not complete order');
    } finally { setCompleting(false); }
  };

  // ── CANCEL ORDER ────────────────────────────────────────────────────────────
  const cancelOrder = async () => {
    const result = await Swal.fire({
      title: 'Cancel this order?',
      html: `
        <div style="text-align:center;padding:4px 0">
          <p style="color:#374151;font-size:14px;margin-bottom:8px">
            Cancel <strong>${order.orderNumber}</strong>?
          </p>
          <p style="font-size:13px;color:#6b7280">Stock will NOT be affected. This cannot be undone.</p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Cancel Order',
      cancelButtonText: 'Keep Order',
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#6b7280',
      customClass: { container: 'swal2-on-top', popup: 'rounded-2xl' },
    });

    if (!result.isConfirmed) return;
    setCancelling(true);

    try {
      const now = Date.now();
      const updates = { orderStatus: 'cancelled', updatedAt: now };
      await update(ref(database, `orders/${order.id}`), updates);

      if (order.userId && order.userId !== 'guest') {
        await update(ref(database, `users/${order.userId}/recentOrders/${order.id}`), {
          status: 'cancelled',
        }).catch(() => {});
      }

      const updated = { ...order, ...updates };
      setOrder(updated);
      onUpdated(updated);
      toast('success', 'Order cancelled');
    } catch (e) {
      console.error(e);
      toast('error', 'Could not cancel order');
    } finally { setCancelling(false); }
  };

  // ── UPDATE ORDER STATUS (intermediate steps) ────────────────────────────────
  const updateOrderStatus = async (newStatus) => {
    try {
      const now = Date.now();
      const extra = {};
      if (newStatus === 'processed') extra.processedAt = now;
      if (newStatus === 'ready')     extra.readyAt     = now;

      await update(ref(database, `orders/${order.id}`), {
        orderStatus: newStatus, updatedAt: now, ...extra,
      });

      if (order.userId && order.userId !== 'guest') {
        await update(ref(database, `users/${order.userId}/recentOrders/${order.id}`), {
          status: newStatus,
        }).catch(() => {});
      }

      const updated = { ...order, orderStatus: newStatus, ...extra };
      setOrder(updated);
      onUpdated(updated);
      toast('success', `Status updated to ${ORDER_STATUS[newStatus]?.label}`);
    } catch (e) {
      toast('error', 'Could not update status');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0"
      style={{ zIndex: 9999, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}>
      <motion.div
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 280 }}
        className="flex flex-col bg-white shadow-2xl"
        style={{ position:"fixed", inset:0, zIndex: 10000 }}
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex-shrink-0 flex items-center justify-between px-6 py-4"
          style={{ background: 'linear-gradient(135deg,#1a1a2e 0%,#800020 100%)' }}>
          <div className="flex items-center gap-3">
            <div>
              <h2 className="text-base font-extrabold text-white">{order.orderNumber}</h2>
              <p className="text-white/55 text-xs mt-0.5">
                {fmtDate(order.createdAt)} · {order.userInfo?.name || order.userId}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge type="order" value={order.orderStatus} small />
            <button onClick={onClose}
              className="w-9 h-9 rounded-xl flex items-center justify-center text-white transition-all ml-2"
              style={{ background: 'rgba(255,255,255,0.12)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.22)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}>
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Status progress bar */}
        {!isTerminal && (
          <div className="flex-shrink-0 px-6 py-3 bg-gray-50 border-b border-gray-100">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Order Progress</p>
            <div className="flex items-center gap-1">
              {['pending','processed','ready','collected'].map((s, i) => {
                const steps = ['pending','processed','ready','collected'];
                const currentIdx = steps.indexOf(order.orderStatus);
                const isActive = i <= currentIdx;
                const isCurrent = i === currentIdx;
                const st = ORDER_STATUS[s];
                return (
                  <React.Fragment key={s}>
                    <button
                      onClick={() => !isTerminal && s !== 'collected' && s !== order.orderStatus && updateOrderStatus(s)}
                      disabled={isTerminal || s === order.orderStatus || s === 'collected'}
                      className="flex flex-col items-center gap-1 flex-shrink-0"
                      title={`Move to ${st.label}`}>
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all"
                        style={{
                          background: isActive ? st.color : '#e5e7eb',
                          color: isActive ? '#fff' : '#9ca3af',
                          boxShadow: isCurrent ? `0 0 0 3px ${st.color}33` : 'none',
                        }}>
                        {i + 1}
                      </div>
                      <span className="text-[9px] font-semibold whitespace-nowrap"
                        style={{ color: isActive ? st.color : '#9ca3af' }}>
                        {st.label}
                      </span>
                    </button>
                    {i < 3 && (
                      <div className="flex-1 h-0.5 mb-4 rounded"
                        style={{ background: i < currentIdx ? st.color : '#e5e7eb' }} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex-shrink-0 flex border-b border-gray-100 px-6 overflow-x-auto scrollbar-none">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className="flex items-center gap-1.5 px-4 py-3.5 text-sm font-semibold border-b-2 transition-all flex-shrink-0"
              style={{
                borderColor: activeTab === t.key ? '#800020' : 'transparent',
                color:       activeTab === t.key ? '#800020' : '#9ca3af',
              }}>
              {t.icon}{t.label}
            </button>
          ))}
        </div>

        {/* Tab body */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            <AnimatePresence mode="wait">

              {/* CONTACT TAB */}
              {activeTab === 'contact' && (
                <motion.div key="contact" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}
                  className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Full Name</label>
                      <input value={contact.name} onChange={e => setContact(c => ({ ...c, name: e.target.value }))}
                        className={inputCls} placeholder="Customer name"
                        onFocus={e => e.target.style.borderColor = '#800020'}
                        onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Company</label>
                      <input value={contact.company} onChange={e => setContact(c => ({ ...c, company: e.target.value }))}
                        className={inputCls} placeholder="Optional"
                        onFocus={e => e.target.style.borderColor = '#800020'}
                        onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Email</label>
                    <div className="relative">
                      <EnvelopeIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input value={contact.email} onChange={e => setContact(c => ({ ...c, email: e.target.value }))}
                        type="email" className={`${inputCls} pl-10`} placeholder="email@example.com"
                        onFocus={e => e.target.style.borderColor = '#800020'}
                        onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Phone</label>
                    <div className="relative">
                      <PhoneIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input value={contact.phone} onChange={e => setContact(c => ({ ...c, phone: e.target.value }))}
                        type="tel" className={`${inputCls} pl-10`} placeholder="0712 345 678"
                        onFocus={e => e.target.style.borderColor = '#800020'}
                        onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                    </div>
                  </div>
                  {/* User ID info */}
                  <div className="p-3 rounded-xl bg-gray-50 border border-gray-100">
                    <p className="text-xs text-gray-500">
                      <span className="font-semibold">User ID:</span> {order.userId || '—'} ·{' '}
                      <span className="font-semibold">Source:</span> {order.orderSource || '—'}
                    </p>
                  </div>
                </motion.div>
              )}

              {/* DELIVERY TAB */}
              {activeTab === 'delivery' && (
                <motion.div key="delivery" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}
                  className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Street Address</label>
                    <div className="relative">
                      <MapPinIcon className="absolute left-3.5 top-3.5 h-4 w-4 text-gray-400" />
                      <input value={delivery.address} onChange={e => setDelivery(d => ({ ...d, address: e.target.value }))}
                        className={`${inputCls} pl-10`} placeholder="Street address"
                        onFocus={e => e.target.style.borderColor = '#800020'}
                        onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Area / Estate</label>
                      <input value={delivery.area} onChange={e => setDelivery(d => ({ ...d, area: e.target.value }))}
                        className={inputCls} placeholder="e.g. Westlands"
                        onFocus={e => e.target.style.borderColor = '#800020'}
                        onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">City</label>
                      <input value={delivery.city} onChange={e => setDelivery(d => ({ ...d, city: e.target.value }))}
                        className={inputCls} placeholder="Nairobi"
                        onFocus={e => e.target.style.borderColor = '#800020'}
                        onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Landmark</label>
                    <input value={delivery.landmark} onChange={e => setDelivery(d => ({ ...d, landmark: e.target.value }))}
                      className={inputCls} placeholder="Nearby landmark"
                      onFocus={e => e.target.style.borderColor = '#800020'}
                      onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Notes</label>
                    <textarea value={delivery.notes} onChange={e => setDelivery(d => ({ ...d, notes: e.target.value }))}
                      rows={3} className={`${inputCls} resize-none`} placeholder="Delivery instructions"
                      onFocus={e => e.target.style.borderColor = '#800020'}
                      onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Preferred Contact</label>
                    <div className="grid grid-cols-2 gap-3">
                      {[{ val: 'phone', label: 'Phone / WhatsApp' }, { val: 'email', label: 'Email' }].map(({ val, label }) => (
                        <button key={val} type="button" onClick={() => setDelivery(d => ({ ...d, contactPref: val }))}
                          className="py-2.5 rounded-xl border-2 text-sm font-semibold transition-all"
                          style={{
                            borderColor: delivery.contactPref === val ? '#800020' : '#e5e7eb',
                            background:  delivery.contactPref === val ? '#fef2f2' : '#fff',
                            color:       delivery.contactPref === val ? '#800020' : '#6b7280',
                          }}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}

              {/* ITEMS TAB */}
              {activeTab === 'items' && (
                <motion.div key="items" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}
                  className="space-y-3">
                  {(order.items || []).map((item, i) => (
                    <div key={i} className="flex items-center gap-3 p-4 bg-gray-50 rounded-xl border border-gray-100">
                      <div className="w-14 h-14 rounded-xl bg-white border border-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {item.image
                          ? <img src={item.image} alt={item.name} className="w-full h-full object-contain p-1" />
                          : <TagIcon className="h-6 w-6 text-gray-300" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800">{item.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Qty: <strong>{item.quantity}</strong> × KES {item.price?.toLocaleString()}
                          {item.variant?.color && ` · Color: ${item.variant.color}`}
                          {item.variant?.size  && ` · Size: ${item.variant.size}`}
                        </p>
                        <p className="text-[10px] text-gray-400 mt-0.5 font-mono">ID: {item.productId}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-bold" style={{ color: '#800020' }}>
                          KES {(item.price * item.quantity)?.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}

                  {/* Totals */}
                  <div className="bg-white rounded-2xl p-4 border border-gray-100 space-y-2 mt-4">
                    {order.savings > 0 && (
                      <div className="flex justify-between text-xs">
                        <span className="text-green-600 font-semibold">Savings</span>
                        <span className="font-bold text-green-600">- KES {order.savings?.toLocaleString()}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Subtotal</span>
                      <span className="font-semibold">KES {order.subtotal?.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                      <span className="font-extrabold text-gray-900">Total</span>
                      <span className="text-xl font-extrabold" style={{ color: '#800020' }}>KES {order.total?.toLocaleString()}</span>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* PAYMENT TAB */}
              {activeTab === 'payment' && (
                <motion.div key="payment" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}
                  className="space-y-5">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Payment Method</label>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {PAYMENT_METHODS.map(({ val, label }) => (
                        <button key={val} type="button" onClick={() => setPayment(p => ({ ...p, paymentMethod: val }))}
                          className="py-2.5 px-3 rounded-xl border-2 text-xs font-semibold transition-all text-center"
                          style={{
                            borderColor: payment.paymentMethod === val ? '#800020' : '#e5e7eb',
                            background:  payment.paymentMethod === val ? '#fef2f2' : '#fff',
                            color:       payment.paymentMethod === val ? '#800020' : '#6b7280',
                          }}>
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Payment Status</label>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {Object.entries(PAYMENT_STATUS).map(([val, cfg]) => (
                        <button key={val} type="button" onClick={() => setPayment(p => ({ ...p, paymentStatus: val }))}
                          className="py-2.5 px-3 rounded-xl border-2 text-xs font-semibold transition-all text-center"
                          style={{
                            borderColor: payment.paymentStatus === val ? cfg.color : '#e5e7eb',
                            background:  payment.paymentStatus === val ? cfg.bg    : '#fff',
                            color:       payment.paymentStatus === val ? cfg.color : '#6b7280',
                          }}>
                          {cfg.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5">Payment Reference</label>
                    <input value={payment.paymentReference} onChange={e => setPayment(p => ({ ...p, paymentReference: e.target.value }))}
                      className={inputCls} placeholder="e.g. M-Pesa code, transaction ID"
                      onFocus={e => e.target.style.borderColor = '#800020'}
                      onBlur={e => e.target.style.borderColor = '#e5e7eb'} />
                  </div>

                  {/* Current summary */}
                  <div className="p-4 rounded-xl bg-gray-50 border border-gray-100">
                    <div className="flex gap-4">
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider font-bold mb-1">Current Method</p>
                        <StatusBadge type="payment" value={order.paymentStatus} small />
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase tracking-wider font-bold mb-1">Order Total</p>
                        <p className="text-sm font-extrabold" style={{ color: '#800020' }}>KES {order.total?.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* TIMELINE TAB */}
              {activeTab === 'timeline' && (
                <motion.div key="timeline" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}
                  className="space-y-3">
                  {[
                    { label: 'Order Placed',     ts: order.createdAt,   show: true,                  color: '#800020' },
                    { label: 'Processed',        ts: order.processedAt, show: !!order.processedAt,   color: '#3b82f6' },
                    { label: 'Ready for Pickup', ts: order.readyAt,     show: !!order.readyAt,        color: '#8b5cf6' },
                    { label: 'Completed',        ts: order.collectedAt, show: !!order.collectedAt,    color: '#16a34a' },
                  ].map((t, i) => (
                    <div key={i} className={`flex items-center gap-4 p-3 rounded-xl ${t.show ? 'bg-gray-50' : 'opacity-30'}`}>
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: t.show ? t.color : '#d1d5db' }} />
                      <span className="text-sm font-semibold text-gray-700 flex-1">{t.label}</span>
                      <span className="text-xs text-gray-400">{t.show ? fmt(t.ts) : 'Not yet'}</span>
                    </div>
                  ))}

                  {order.orderStatus === 'cancelled' && (
                    <div className="flex items-center gap-4 p-3 rounded-xl bg-red-50">
                      <div className="w-3 h-3 rounded-full flex-shrink-0 bg-red-500" />
                      <span className="text-sm font-semibold text-red-700 flex-1">Cancelled</span>
                      <span className="text-xs text-red-400">{fmt(order.updatedAt)}</span>
                    </div>
                  )}

                  {/* Meta */}
                  <div className="mt-4 p-4 rounded-xl bg-gray-50 border border-gray-100 space-y-1.5">
                    <p className="text-xs text-gray-500"><span className="font-semibold">Order Number:</span> {order.orderNumber}</p>
                    <p className="text-xs text-gray-500"><span className="font-semibold">Order Source:</span> {order.orderSource || '—'}</p>
                    <p className="text-xs text-gray-500"><span className="font-semibold">User ID:</span> {order.userId || '—'}</p>
                    <p className="text-xs text-gray-500"><span className="font-semibold">Created:</span> {fmt(order.createdAt)}</p>
                  </div>
                </motion.div>
              )}

            </AnimatePresence>
          </div>
        </div>

        {/* Footer actions */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 bg-white">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Complete */}
            {!isTerminal && (
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                onClick={completeOrder} disabled={completing || cancelling}
                className="flex-1 py-3 rounded-2xl text-sm font-bold text-white flex items-center justify-center gap-2 shadow disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg,#15803d,#16a34a)' }}>
                {completing
                  ? <><ArrowPathIcon className="h-4 w-4 animate-spin" />Completing…</>
                  : <><CheckCircleSolid className="h-4 w-4" />Complete Order</>
                }
              </motion.button>
            )}

            {/* Cancel */}
            {!isTerminal && (
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                onClick={cancelOrder} disabled={completing || cancelling}
                className="flex-1 py-3 rounded-2xl text-sm font-bold text-white flex items-center justify-center gap-2 shadow disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg,#dc2626,#ef4444)' }}>
                {cancelling
                  ? <><ArrowPathIcon className="h-4 w-4 animate-spin" />Cancelling…</>
                  : <><XCircleSolid className="h-4 w-4" />Cancel Order</>
                }
              </motion.button>
            )}

            {/* Save changes */}
            {activeTab !== 'items' && activeTab !== 'timeline' && !isTerminal && (
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.97 }}
                onClick={saveChanges} disabled={saving}
                className="flex-1 py-3 rounded-2xl text-sm font-bold text-white flex items-center justify-center gap-2 shadow disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg,#800020,#b30000)' }}>
                {saving ? 'Saving…' : 'Save Changes'}
              </motion.button>
            )}

            <button onClick={onClose}
              className="sm:flex-none px-6 py-3 rounded-2xl text-sm font-semibold border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all">
              Close
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// ─── ORDER ROW ────────────────────────────────────────────────────────────────
const OrderRow = ({ order, idx, onClick }) => {
  const oSt = ORDER_STATUS[order.orderStatus]    || { label: order.orderStatus, color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb' };
  const pSt = PAYMENT_STATUS[order.paymentStatus] || { label: order.paymentStatus, color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb' };
  const totalItems = (order.items || []).reduce((s, i) => s + (i.quantity || 1), 0);

  return (
    <motion.tr
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: idx * 0.015 }}
      className="border-b border-gray-50 hover:bg-gray-50/70 cursor-pointer transition-colors"
      onClick={() => onClick(order)}>
      <td className="px-4 py-3">
        <p className="text-sm font-bold text-gray-900">{order.orderNumber}</p>
        <p className="text-xs text-gray-400 mt-0.5">{fmtDate(order.createdAt)}</p>
      </td>
      <td className="px-4 py-3 hidden sm:table-cell">
        <p className="text-sm font-semibold text-gray-700 truncate max-w-[140px]">{order.userInfo?.name || '—'}</p>
        <p className="text-xs text-gray-400 truncate max-w-[140px]">{order.userInfo?.phone || order.userInfo?.email || '—'}</p>
      </td>
      <td className="px-4 py-3 hidden md:table-cell">
        <p className="text-xs text-gray-600 truncate max-w-[140px]">
          {(order.items || [])[0]?.name || '—'}
          {(order.items || []).length > 1 && ` +${(order.items || []).length - 1}`}
        </p>
        <p className="text-[10px] text-gray-400">{totalItems} item{totalItems !== 1 ? 's' : ''}</p>
      </td>
      <td className="px-4 py-3">
        <p className="text-sm font-bold" style={{ color: '#800020' }}>KES {order.total?.toLocaleString()}</p>
      </td>
      <td className="px-4 py-3">
        <StatusBadge type="order" value={order.orderStatus} small />
      </td>
      <td className="px-4 py-3 hidden lg:table-cell">
        <StatusBadge type="payment" value={order.paymentStatus} small />
      </td>
      <td className="px-4 py-3 hidden xl:table-cell">
        <span className="text-xs text-gray-500">{order.orderSource?.replace(/_/g, ' ') || '—'}</span>
      </td>
      <td className="px-4 py-3 text-center">
        <ChevronRightIcon className="h-4 w-4 text-gray-400 mx-auto" />
      </td>
    </motion.tr>
  );
};

// ─── SIDEBAR FILTER ───────────────────────────────────────────────────────────
const SidebarFilter = ({ filters, setFilters, counts, onClear }) => (
  <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" style={{ width: 220 }}>
    <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
      <span className="text-xs font-bold text-gray-700 uppercase tracking-wide">Filters</span>
      <button onClick={onClear} className="text-xs text-red-500 font-semibold hover:text-red-700 transition-colors">Clear</button>
    </div>

    <div className="p-3 overflow-y-auto" style={{ maxHeight: '70vh' }}>
      {/* Order Status */}
      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-1 mb-1.5 mt-1">Order Status</p>
      {[{ val: 'all', label: 'All', color: '#6b7280' }, ...Object.entries(ORDER_STATUS).map(([val, cfg]) => ({ val, label: cfg.label, color: cfg.color }))].map(s => (
        <button key={s.val} onClick={() => setFilters(f => ({ ...f, status: s.val }))}
          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-medium transition-all mb-0.5"
          style={{
            background: filters.status === s.val ? s.color + '18' : 'transparent',
            color:      filters.status === s.val ? s.color : '#6b7280',
            fontWeight: filters.status === s.val ? 700 : 500,
          }}>
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: s.color }} />
          {s.label}
          {counts.status?.[s.val] != null && (
            <span className="ml-auto text-[10px] font-bold opacity-60">{counts.status[s.val]}</span>
          )}
        </button>
      ))}

      {/* Payment Status */}
      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-1 mb-1.5 mt-4">Payment</p>
      {[{ val: 'all', label: 'All', color: '#6b7280' }, ...Object.entries(PAYMENT_STATUS).map(([val, cfg]) => ({ val, label: cfg.label, color: cfg.color }))].map(s => (
        <button key={s.val} onClick={() => setFilters(f => ({ ...f, payment: s.val }))}
          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-medium transition-all mb-0.5"
          style={{
            background: filters.payment === s.val ? s.color + '18' : 'transparent',
            color:      filters.payment === s.val ? s.color : '#6b7280',
            fontWeight: filters.payment === s.val ? 700 : 500,
          }}>
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: s.color }} />
          {s.label}
        </button>
      ))}

      {/* Order Source */}
      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-1 mb-1.5 mt-4">Source</p>
      {[{ val: 'all', label: 'All Sources' }, ...ORDER_SOURCES].map(s => (
        <button key={s.val} onClick={() => setFilters(f => ({ ...f, source: s.val }))}
          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-medium transition-all mb-0.5"
          style={{
            background: filters.source === s.val ? '#80002018' : 'transparent',
            color:      filters.source === s.val ? '#800020'   : '#6b7280',
            fontWeight: filters.source === s.val ? 700 : 500,
          }}>
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: filters.source === s.val ? '#800020' : '#d1d5db' }} />
          <span className="truncate">{s.label}</span>
        </button>
      ))}

      {/* Payment Method */}
      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-1 mb-1.5 mt-4">Method</p>
      {[{ val: 'all', label: 'All Methods' }, ...PAYMENT_METHODS].map(s => (
        <button key={s.val} onClick={() => setFilters(f => ({ ...f, method: s.val }))}
          className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-medium transition-all mb-0.5"
          style={{
            background: filters.method === s.val ? '#80002018' : 'transparent',
            color:      filters.method === s.val ? '#800020'   : '#6b7280',
            fontWeight: filters.method === s.val ? 700 : 500,
          }}>
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: filters.method === s.val ? '#800020' : '#d1d5db' }} />
          <span className="truncate">{s.label}</span>
        </button>
      ))}
    </div>
  </div>
);

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN ORDER PROCESSING
// ═══════════════════════════════════════════════════════════════════════════════
const OrderProcessing = () => {
  const { userData } = useAuth();
  const userRole = (userData?.personalInfo?.role || '').toLowerCase();
  const isAuthorized = userRole === 'admin' || userRole === 'superadmin';

  const [orders,        setOrders]        = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [sidebarOpen,   setSidebarOpen]   = useState(true);
  const [search,        setSearch]        = useState('');
  const [filters, setFilters] = useState({
    status:  'all',
    payment: 'all',
    source:  'all',
    method:  'all',
  });

  // ─── LOAD ALL ORDERS ────────────────────────────────────────────────────────
  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await get(ref(database, 'orders'));
      if (!snap.exists()) { setOrders([]); setLoading(false); return; }

      const all = Object.entries(snap.val())
        .map(([id, data]) => ({ id, ...data }))
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

      setOrders(all);
    } catch (e) {
      console.error('Load orders error:', e);
      toast('error', 'Could not load orders');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  // ─── FILTER ─────────────────────────────────────────────────────────────────
  const filtered = orders.filter(o => {
    if (filters.status  !== 'all' && o.orderStatus    !== filters.status)  return false;
    if (filters.payment !== 'all' && o.paymentStatus  !== filters.payment) return false;
    if (filters.source  !== 'all' && o.orderSource    !== filters.source)  return false;
    if (filters.method  !== 'all' && o.paymentMethod  !== filters.method)  return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      const matchOrder  = o.orderNumber?.toLowerCase().includes(q);
      const matchName   = o.userInfo?.name?.toLowerCase().includes(q);
      const matchPhone  = o.userInfo?.phone?.toLowerCase().includes(q);
      const matchEmail  = o.userInfo?.email?.toLowerCase().includes(q);
      const matchItem   = (o.items || []).some(i => i.name?.toLowerCase().includes(q));
      if (!matchOrder && !matchName && !matchPhone && !matchEmail && !matchItem) return false;
    }
    return true;
  });

  // Counts for sidebar
  const statusCounts = { all: orders.length };
  ORDER_STATUSES.forEach(s => { statusCounts[s] = orders.filter(o => o.orderStatus === s).length; });

  // Stats
  const stats = {
    total:   orders.length,
    pending: orders.filter(o => o.orderStatus === 'pending').length,
    unpaid:  orders.filter(o => o.paymentStatus === 'pending' && o.orderStatus !== 'cancelled').length,
    revenue: orders.filter(o => o.paymentStatus === 'paid').reduce((s, o) => s + (o.total || 0), 0),
  };

  const handleOrderUpdated = (updated) => {
    setOrders(prev => prev.map(o => o.id === updated.id ? updated : o));
    if (selectedOrder?.id === updated.id) setSelectedOrder(updated);
  };

  if (!isAuthorized) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4" style={{ background: '#f8f9fa' }}>
        <ShoppingBagIcon className="h-16 w-16 text-gray-200" />
        <h2 className="text-xl font-bold text-gray-700">Access Denied</h2>
        <p className="text-gray-500 text-sm">You need admin or superadmin privileges to access Order Processing.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg,#f8f9fa 0%,#e9ecef 100%)' }}>
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-8">

        {/* HEADER */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg,#800020,#b30000)' }}>
              <ShoppingBagIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-gray-900">Order Processing</h1>
                <span className="px-2.5 py-1 rounded-lg text-xs font-bold uppercase tracking-wide"
                  style={{
                    background: userRole === 'superadmin' ? '#1a1a2e' : '#fef2f2',
                    color:      userRole === 'superadmin' ? '#fff'     : '#800020',
                  }}>
                  {userRole === 'superadmin' ? '⚡ Super Admin' : '🛡 Admin'}
                </span>
              </div>
              <p className="text-gray-500 text-sm mt-0.5">Manage, edit and complete customer orders</p>
            </div>
          </div>
          <button onClick={loadOrders}
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-gray-500 hover:text-gray-800 shadow-sm transition-all">
            <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* STAT PILLS */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total Orders',   val: stats.total,                   color: '#1a1a2e' },
            { label: 'Pending',        val: stats.pending,                  color: '#f59e0b' },
            { label: 'Awaiting Payment', val: stats.unpaid,                 color: '#ef4444' },
            { label: 'Revenue (Paid)',  val: `KES ${fmtNum(stats.revenue)}`, color: '#16a34a' },
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl border border-gray-100 shadow-sm px-4 py-3">
              <p className="text-xl font-bold" style={{ color: s.color }}>{s.val}</p>
              <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* LAYOUT */}
        <div className="flex gap-5 items-start">

          {/* SIDEBAR */}
          <AnimatePresence initial={false}>
            {sidebarOpen && (
              <motion.div
                key="op-sidebar"
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 220, opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{ type: 'spring', damping: 26, stiffness: 280 }}
                style={{ flexShrink: 0, overflow: 'hidden' }}>
                <SidebarFilter
                  filters={filters}
                  setFilters={setFilters}
                  counts={{ status: statusCounts }}
                  onClear={() => setFilters({ status: 'all', payment: 'all', source: 'all', method: 'all' })}
                />
              </motion.div>
            )}
          </AnimatePresence>

          {/* MAIN TABLE */}
          <div className="flex-1 min-w-0">
            {/* Toolbar */}
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              {!sidebarOpen && (
                <button onClick={() => setSidebarOpen(true)}
                  className="w-9 h-9 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-gray-500 hover:text-gray-800 shadow-sm transition-all flex-shrink-0">
                  <Bars3Icon className="h-4 w-4" />
                </button>
              )}
              {sidebarOpen && (
                <button onClick={() => setSidebarOpen(false)}
                  className="w-9 h-9 flex items-center justify-center rounded-xl bg-white border border-gray-200 text-gray-500 hover:text-gray-800 shadow-sm transition-all flex-shrink-0">
                  <ChevronLeftIcon className="h-4 w-4" />
                </button>
              )}
              <div className="flex-1 relative" style={{ minWidth: 180 }}>
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                <input value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Search order #, customer, product…"
                  className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none shadow-sm" />
              </div>
              {search && (
                <button onClick={() => setSearch('')}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 bg-white border border-gray-200">
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </div>

            <p className="text-xs text-gray-400 mb-3 font-medium">
              {filtered.length} order{filtered.length !== 1 ? 's' : ''} of {orders.length} total
            </p>

            {/* TABLE */}
            {loading ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-gray-100 shadow-sm">
                <div className="inline-block w-8 h-8 border-4 border-red-200 border-t-red-700 rounded-full animate-spin" />
                <p className="text-gray-500 mt-3 text-sm">Loading orders…</p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-100 shadow-sm">
                <ShoppingBagIcon className="h-12 w-12 text-gray-200 mx-auto mb-3" />
                <p className="text-gray-500 font-medium text-sm">No orders match your filters</p>
                <button
                  onClick={() => { setSearch(''); setFilters({ status: 'all', payment: 'all', source: 'all', method: 'all' }); }}
                  className="mt-3 text-sm font-semibold" style={{ color: '#800020' }}>
                  Clear filters
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr style={{ background: 'linear-gradient(135deg,#1a1a2e,#800020)' }}>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wide">Order</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wide hidden sm:table-cell">Customer</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wide hidden md:table-cell">Items</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wide">Total</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wide">Status</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wide hidden lg:table-cell">Payment</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-white/80 uppercase tracking-wide hidden xl:table-cell">Source</th>
                        <th className="px-4 py-3 w-8" />
                      </tr>
                    </thead>
                    <tbody>
                      <AnimatePresence>
                        {filtered.map((order, idx) => (
                          <OrderRow
                            key={order.id}
                            order={order}
                            idx={idx}
                            onClick={setSelectedOrder}
                          />
                        ))}
                      </AnimatePresence>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ORDER DETAIL MODAL */}
      <AnimatePresence>
        {selectedOrder && (
          <OrderDetailModal
            order={selectedOrder}
            onClose={() => setSelectedOrder(null)}
            onUpdated={handleOrderUpdated}
          />
        )}
      </AnimatePresence>

      <style>{`
        .scrollbar-none::-webkit-scrollbar{display:none;}
        .scrollbar-none{-ms-overflow-style:none;scrollbar-width:none;}
      `}</style>
    </div>
  );
};

export default OrderProcessing;